import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { COMPLETE_APP_ONBOARDING_STEP_SUCCESS } from "../../modules/constants";
import { AnimatePresence } from "../AuthNew/shared/motionUtils";
import AppOnboardingExpanded from "./AppOnboardingExpanded";
import AppOnboardingPanel from "./AppOnboardingPanel";
import CompletionModal from "./CompletionModal";
import { getFilteredSteps } from "./shared/stepsConfig";


const ALLOWED_ROLES = ["owner", "admin"];

/**
 * AppOnboardingWrapper — Smart Container
 *
 * The only component in this module that holds business logic.
 * All UI is delegated to AppOnboardingPanel (collapsed strip) and
 * AppOnboardingExpanded (full step view).
 *
 * Props supplied via Redux connect() in index.js:
 *   appOnboardingState, fetchAppOnboarding, pollAppOnboardingProgress, completeAppOnboardingStep
 *
 * Props passed down from Admin.jsx (parent):
 *   selectedOrganization, onInviteMember, isInviteLoading, children
 */
const AppOnboardingWrapper = ({
  // Redux state
  appOnboardingState,
  // Redux actions
  fetchAppOnboarding,
  pollAppOnboardingProgress,
  completeAppOnboardingStep,
  skipAppOnboardingStep,
  showCompletionModal,
  hideCompletionModal,
  updateDiscountModalShown,
  // Parent props
  selectedOrganization,
  onInviteMember,
  isInviteLoading,
  isMobile,
  isTablet,
  children,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const orgType = selectedOrganization?.type || "operation"
  const isOperation = orgType === "operation";
  const isPostOnboardingVisible = useSelector(state => state.onboardingNew?.isPostOnboardingVisible ?? false);

  // -------------------------------------------------------------------------
  // Expand / collapse state — start ALWAYS collapsed, no session persistence
  // -------------------------------------------------------------------------
  const [isExpanded, setIsExpanded] = useState(true);

  const expandedRef = useRef(null);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Click-outside-to-collapse
  useEffect(() => {
    if (!isExpanded) return;
    const handleMouseDown = (e) => {
      if (expandedRef.current && !expandedRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isExpanded]);

  // -------------------------------------------------------------------------
  // Current step navigation — tracks which step user is viewing
  // -------------------------------------------------------------------------
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // currentStepIndex drives navigation only:
  //   — which step card is displayed
  //   — which dot gets the active highlight
  // It has NO effect on the badge, ring, progress bar, tick icons, or connecting lines.
  // Those are all driven by backendCompletedCount (see below).

  // -------------------------------------------------------------------------
  // Role gate — only owners and admins see the panel
  // -------------------------------------------------------------------------
  const role = selectedOrganization?.role;
  const isAllowedRole = ALLOWED_ROLES.includes(role);

  // -------------------------------------------------------------------------
  // Fetch on org change
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (selectedOrganization?.id && isAllowedRole) {
      fetchAppOnboarding(selectedOrganization.id);
    }
  }, [selectedOrganization?.id, isAllowedRole, fetchAppOnboarding]);

  // -------------------------------------------------------------------------
  // Derive filtered steps (org-type aware)
  // -------------------------------------------------------------------------
  const filteredSteps = useMemo(
    () => getFilteredSteps(selectedOrganization),
    [selectedOrganization?.id, selectedOrganization?.type],
  );

  // Merge API completion state into the static step config
  const stepsWithStatus = useMemo(() => {
    const apiSteps = appOnboardingState?.steps ?? [];
    const statusMap = {};
    apiSteps.forEach(s => {
      statusMap[s.name] = {
        completed: s.status === "completed",
        skipped: s.status === "skipped",
      };
    });

    return filteredSteps.map(step => ({
      ...step,
      completed: statusMap[step.id]?.completed ?? false,
      skipped: statusMap[step.id]?.skipped ?? false,
    }));
  }, [filteredSteps, appOnboardingState?.steps]);

  // First PENDING step index (not first incomplete) — skipped steps don't count as active
  // This ensures users resume from where they left off, skipping previously skipped steps
  const activeStepIndex = useMemo(
    () => stepsWithStatus.findIndex(s => !s.completed && !s.skipped),
    [stepsWithStatus],
  );

  // Backend-confirmed COMPLETED count only (skipped steps don't count toward progress)
  // This is the ONLY input to badge, ring, progress bar, and tick icons
  const backendCompletedCount = useMemo(
    () => stepsWithStatus.filter(s => s.completed).length,
    [stepsWithStatus],
  );

  // Check if all steps are completed
  const allStepsCompleted = activeStepIndex === -1;

  // -------------------------------------------------------------------------
  // Auto-jump currentStepIndex to first pending step when status changes
  // This ensures when user completes or skips a step, focus moves to first pending
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (stepsWithStatus.length > 0) {
      const firstPending = stepsWithStatus.findIndex(s => !s.completed && !s.skipped);
      setCurrentStepIndex(firstPending === -1 ? 0 : firstPending);
    }
  }, [stepsWithStatus]); // Reset when steps status changes

  // -------------------------------------------------------------------------
  // Show completion modal when all steps are done
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (
      appOnboardingState.isCompleted &&
      appOnboardingState.discountEarned &&
      !appOnboardingState.isCompletionModalOpen &&
      !appOnboardingState.discountModalShown
    ) {
      // Slight delay to ensure smooth transition
      const timer = setTimeout(() => {
        showCompletionModal();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [appOnboardingState.isCompleted, appOnboardingState.isCompletionModalOpen, appOnboardingState.discountModalShown, showCompletionModal]);

  // -------------------------------------------------------------------------
  // Fetch backend progress when the panel is expanded and the active step is complete_job.
  // This avoids continuous polling and updates status only on expand/state changes.
  // -------------------------------------------------------------------------
  const isPollingCompleteJob = useMemo(
    () => stepsWithStatus[activeStepIndex]?.id === "complete_job",
    [stepsWithStatus, activeStepIndex],
  );

  useEffect(() => {
    if (!selectedOrganization?.id || !isPollingCompleteJob || !isExpanded) return undefined;

    pollAppOnboardingProgress(selectedOrganization.id);
    return undefined;
  }, [selectedOrganization?.id, isPollingCompleteJob, isExpanded, pollAppOnboardingProgress]);

  // -------------------------------------------------------------------------
  // Close completion modal if discount modal has been shown
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (
      appOnboardingState.discountModalShown &&
      appOnboardingState.isCompletionModalOpen
    ) {
      // Hide completion modal since discount modal is now shown
      hideCompletionModal();
    }
  }, [appOnboardingState.discountModalShown, appOnboardingState.isCompletionModalOpen]);

  // -------------------------------------------------------------------------
  // CSS variable — tells the sidebar + main-panel how much bottom space to
  // reserve for the collapsed strip. Resets to 0 when expanded (the expanded
  // overlay intentionally sits over content) or when the panel is hidden.
  // -------------------------------------------------------------------------
  const panelCollapsedAndVisible = useMemo(
    () =>
      isAllowedRole &&
      appOnboardingState &&
      !appOnboardingState.isLoading &&
      !appOnboardingState.isCompletionModalOpen &&
      activeStepIndex !== -1 &&
      !isExpanded &&
      !isPostOnboardingVisible,
    [
      isAllowedRole,
      appOnboardingState?.isLoading,
      appOnboardingState?.isCompletionModalOpen,
      activeStepIndex,
      isExpanded,
      isPostOnboardingVisible,
    ],
  );

  const panelHeight = useMemo(
    () =>
      panelCollapsedAndVisible
        ? isMobile
          ? "120px"
          : isTablet
          ? "96px"
          : "104px"
        : "0px",
    [panelCollapsedAndVisible, isMobile, isTablet],
  );

  useEffect(() => {
    // Delay CSS variable change to match animation duration (0.3s)
    // so the layout doesn't jump until the panel/expanded animation completes
    const timer = setTimeout(() => {
      document.documentElement.style.setProperty(
        '--app-onboarding-panel-height',
        panelHeight,
      );
    }, 300);

    return () => {
      clearTimeout(timer);
      document.documentElement.style.setProperty("--app-onboarding-panel-height", "0px");
    };
  }, [panelHeight]);

  // -------------------------------------------------------------------------
  // Step CTA handlers
  // -------------------------------------------------------------------------
  const handleCompleteStep = useCallback(
    stepId => {
      if (selectedOrganization?.id) {
        completeAppOnboardingStep(selectedOrganization.id, stepId);
      }
    },
    [completeAppOnboardingStep, selectedOrganization?.id],
  );

  /**
   * stepCallbacks — maps each step's ctaKey to the relevant handler.
   * Auto-collapses the expanded panel after executing action (CTA button click).
   * Add more entries here as new step CTAs are introduced.
   */
  const stepCallbacks = useMemo(
    () => ({
      inviteMembers: () => {
        history.push("/user/member-invitation");
        setIsExpanded(false); // Auto-collapse after action
        // handleCompleteStep("add_employees");
      },
      addTeam: () => {
        history.push("/user/add-team");
        setIsExpanded(false); // Auto-collapse after action
        // Optimistic update: immediately mark as completed with API call
        // const step = stepsWithStatus.find(s => s.id === "add_team");
        // if (step && !step.completed) {
        //   handleCompleteStep("add_team");
        // }
      },
      addCustomer: () => {
        history.push("/user/customers/add");
        setIsExpanded(false); // Auto-collapse after action
        // handleCompleteStep("add_customer");
      },
      createJob: () => {
        const path = isOperation ? "/user/create-task" : "/user/create-job";
        history.push(path);
        setIsExpanded(false); // Auto-collapse after action
        // handleCompleteStep("create_job");
      },
      scheduleJob: () => {
        history.push("/user/shift-and-schedule");
        setIsExpanded(false); // Auto-collapse after action
        const step = stepsWithStatus.find(s => s.id === "create_schedule");
        if (step && !step.completed) {
          handleCompleteStep("create_schedule");
        }
      },
      installApp: () => {
        // Opens in new tab — no route change
        window.open("https://fieldservicely.com/get-app", "_blank", "noopener");
        setIsExpanded(false); // Auto-collapse after action
        // handleCompleteStep("install_mobile_app");
      },
      completeJob: () => {
        const path = isOperation ? "/user/tasks" : "/user/jobs";
        // Mark complete_job locally only (no API call) — the user just needs to navigate to the list
        // dispatch({ type: COMPLETE_APP_ONBOARDING_STEP_SUCCESS, payload: { stepId: "complete_job" } });
        history.push(path);
        setIsExpanded(false); // Auto-collapse after action
      },
      sendInvoice: () => {
        history.push("/user/invoices");
        setIsExpanded(false); // Auto-collapse after action
        // handleCompleteStep("create_invoice");
      },
    }),
    [handleCompleteStep, history, isOperation, stepsWithStatus],
  );

  const handleSkipStep = useCallback(
    stepId => {
      if (selectedOrganization?.id) {
        // Call backend with skip flag. The subsequent stepsWithStatus update will
        // re-seed currentStepIndex via the useEffect below — no local advance needed.
        skipAppOnboardingStep(selectedOrganization.id, stepId);
      }
    },
    [skipAppOnboardingStep, selectedOrganization?.id],
  );

          // -------------------------------------------------------------------------
  // Step dot click handler — navigate to any step freely (navigation only)
  // Has no effect on badge, ring, progress bar, or connecting lines
  // -------------------------------------------------------------------------
  const handleStepDotClick = useCallback(
    (clickedIndex) => {
      setCurrentStepIndex(clickedIndex);
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Completion modal handlers
  // -------------------------------------------------------------------------
  const handleCloseModal = useCallback(() => {
    hideCompletionModal();
  }, []);

  const handleViewPlans = useCallback(() => {
    hideCompletionModal();
    history.push("/user/subscription");
  }, [hideCompletionModal, history]);

  // -------------------------------------------------`------------------------
  // Step navigation handlers
  // -------------------------------------------------------------------------
  const handlePrevStep = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleNextStep = useCallback(() => {
    setCurrentStepIndex(prev => Math.min(stepsWithStatus.length - 1, prev + 1));
  }, [stepsWithStatus.length]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Always render children so the main app is never blocked
  const panel =
    isAllowedRole &&
    appOnboardingState &&
    !appOnboardingState.isLoading &&
    activeStepIndex !== -1 &&
    !appOnboardingState.isCompletionModalOpen &&
    !isPostOnboardingVisible ? (
      <>
        <AnimatePresence>
          {isExpanded && (
            <AppOnboardingExpanded
              key="app-onboarding-expanded"
              ref={expandedRef}
              filteredSteps={stepsWithStatus}
              completedCount={backendCompletedCount}
              currentStepIndex={currentStepIndex}
              onPrevStep={handlePrevStep}
              onNextStep={handleNextStep}
              onStepDotClick={handleStepDotClick}
              totalSteps={stepsWithStatus.length}
              trialConfig={appOnboardingState.trialConfig}
              stepCallbacks={stepCallbacks}
              onSkipStep={handleSkipStep}
              onToggle={handleToggle}
              isMobile={isMobile}
              isTablet={isTablet}
              selectedOrganization={selectedOrganization}
              completeAppOnboardingStep={completeAppOnboardingStep}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!isExpanded && (
            <AppOnboardingPanel
              key="app-onboarding-panel"
              completedCount={backendCompletedCount}
              currentStepIndex={currentStepIndex}
              totalSteps={stepsWithStatus.length}
              activeStepTitle={stepsWithStatus[currentStepIndex]?.title ?? ""}
              activeStepDescription={stepsWithStatus[currentStepIndex]?.description ?? ""}
              trialConfig={appOnboardingState.trialConfig}
              isExpanded={isExpanded}
              onToggle={handleToggle}
              isMobile={isMobile}
              isTablet={isTablet}
              selectedOrganization={selectedOrganization}
            />
          )}
        </AnimatePresence>
      </>
    ) : null;

  return (
    <>
      {children}
      {panel}
      {/* Completion modal */}
      {appOnboardingState && (
        <CompletionModal
          isOpen={appOnboardingState.isCompletionModalOpen}
          onClose={handleCloseModal}
          onViewPlans={handleViewPlans}
          updateDiscountModalShown={updateDiscountModalShown}
          creditAmount={appOnboardingState.trialConfig?.creditAmount ?? 20}
          creditCurrency={appOnboardingState.trialConfig?.creditCurrency ?? "$"}
          selectedOrganization={selectedOrganization}
        />
      )}
    </>
  );
};

export default AppOnboardingWrapper;